import angular from "angular";

import { ApiItemType, Arrays, calcReportType, Calculation, CalculationRowType, Calculations, Usecase, UsecaseListItem, CalculationStatus, SalaryDateKind, PaymentChannel, InvoicesLogic } from "@salaxy/core";

import { CalculationCrudController } from "../CalculationCrudController";
import { EditDialogKnownActions, InvoicesService, ReportsService, SessionService, UiCrudHelpers, UiHelpers, UsecasesService } from "../../../services";
import { ApiCrudObjectControllerBindings } from "../../bases";

/** Enumerations for calculator actions */
enum CalculatorActions {
  AllowForcePay = "allowForcePay",
  Approve = "approve",
  Cancel = "cancel",
  CopyAsNew = "copyAsNew",
  Delete = "delete",
  Pay = "pay",
  Recalculate = "recalculate",
  Reject = "reject",
  Save = "save",
  SendToPro = "sendToPro",
  ShowPaymentDialog = "showPaymentDialog",
  SetSalaryDateKind = "setSalaryDateKind",
}
/**
 * Year 2022 version of the Calculator controller designed for customized calculator UI's (usecases).
 * Removes old methods that are no longer necessary (household, old calculator UI)
 * and adds more support for customization.
 */
export class Calculator2022Controller extends CalculationCrudController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Calculations", "ReportsService", "UiHelpers", "$location", "$routeParams", "SessionService", "InvoicesService", "UiCrudHelpers", "UsecasesService"];

  /** Component bindings */
  public static crudBindings = (new class extends ApiCrudObjectControllerBindings {
  }());

  /**
   * The usecase definition if specified and loaded.
   * Check the usecaseState if the.
   */
  public usecase: null | Usecase = null;

  /** Available usecases if the user needs to make a selection of which usecase to take. */
  public usecases: UsecaseListItem[];


  /** If true, additional worker info is displayed in the UI */
  public isWorkerInfoOpen: boolean;


  constructor(
    calcApi: Calculations,
    reportsService: ReportsService,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    sessionService: SessionService,
    protected invoicesService: InvoicesService,
    protected uiCrudHelpers: UiCrudHelpers,
    private usecasesService: UsecasesService,
  ) {
    // Dependency injection
    super(calcApi, reportsService, uiHelpers, $location, $routeParams, sessionService, invoicesService);
  }

  /**
   * Initialization code.
   */
  public $onInit() {
    this.isWorkerInfoOpen = false;
    super.$onInit();
  }

  /** Overrides the reload to assure that usecase is also loaded. */
  public reload(): Promise<Calculation> {
    return super.reload().then((calc) => {
      const usecaseUri = this.$location?.search().usecase;
      if (!usecaseUri && this.isNew()) {
        return this.showUsecasesSelection();
      }
      return this.usecasesService.assureUsecase(ApiItemType.Calculation, calc, usecaseUri).then((usecase) => {
        this.usecase = usecase;
        //this.callEvent("onLoad");
        return calc;
      });
    });
  }

  /**
   * If true, the given action (button) is displayed.
   * @param action One of the action values.
   * For now, validation and role based action logic is in UI.
   * Todo: some edge cases, i.e. isForcePayAllAllowed (for pay-button) are still in UI.
   */
  public showAction(action: CalculatorActions): boolean {

    if (!this.current) {
      return false;
    }
    // In read only mode
    if (this.isReadOnlyForced && action != CalculatorActions.ShowPaymentDialog) {
      return false;
    }
    if (this.isReadOnlyForced && action == CalculatorActions.ShowPaymentDialog) {
      return true;
    }
    if (this.current.workflow.status == CalculationStatus.PayrollDraft) {
      // Not possible to make changes to editable payroll calculation
      return false;
    }
    if (this.current.isReadOnly) {
      switch (action) {
        case CalculatorActions.Cancel:
          return this.current.workflow.status == CalculationStatus.PaymentStarted &&
            (
              [
                PaymentChannel.PalkkausManual,
              PaymentChannel.TalenomManual,
                PaymentChannel.PalkkausPersonal,
                PaymentChannel.PalkkausInvoices
              ].indexOf(this.current.info.paymentChannel) > -1 ||
              (
                InvoicesLogic.isCfa(this.current.info.paymentChannel) &&
                !this.current.info.payrollId
              )
            );
        case CalculatorActions.CopyAsNew:
          return !this.isReadOnlyForced; // Not needed?
        case CalculatorActions.ShowPaymentDialog:
          return true;
        case CalculatorActions.AllowForcePay: // "Allow forcepay" action is payment action when calculation is not valid. Logic handled in UI for now.
        case CalculatorActions.Pay:
        case CalculatorActions.Recalculate:
        default:
          return false;
      }
    } else {
      switch (action) {
        case CalculatorActions.AllowForcePay:
          //TODO: refactor this to contain only allowed status, not disallowed
          return this.isForcePayAllAllowed &&
            (this.current.workflow.status != CalculationStatus.WaitingApproval &&
              this.current.workflow.status != CalculationStatus.SharedRejected &&
              this.current.workflow.status != CalculationStatus.SharedWaiting &&
              this.current.workflow.status != CalculationStatus.Repair
            );
        case CalculatorActions.Pay:
          // add here isValid?
          return this.current.workflow.status == CalculationStatus.Draft ||
            this.current.workflow.status == CalculationStatus.ProDraft ||
            this.current.workflow.status == CalculationStatus.SharedApproved;
        case CalculatorActions.Save:
          return this.current.workflow.status == CalculationStatus.Draft ||
            this.current.workflow.status == CalculationStatus.ProDraft ||
            this.current.workflow.status == CalculationStatus.SharedApproved ||
            this.current.workflow.status == CalculationStatus.History ||
            this.current.workflow.status == CalculationStatus.Repair;
        case CalculatorActions.Delete:
          return this.current.workflow.status == CalculationStatus.Draft ||
            this.current.workflow.status == CalculationStatus.ProDraft ||
            this.current.workflow.status == CalculationStatus.SharedRejected ||
            this.current.workflow.status == CalculationStatus.History ||
            this.current.workflow.status == CalculationStatus.Repair;
        case CalculatorActions.SendToPro:
          return this.current.workflow.status == CalculationStatus.WaitingApproval;
        case CalculatorActions.Recalculate:
          return true;
        case CalculatorActions.Reject:
          return this.current.workflow.status == CalculationStatus.SharedApproved ||
            this.current.workflow.status == CalculationStatus.SharedWaiting;
        case CalculatorActions.Approve:
          return this.current.workflow.status == CalculationStatus.SharedRejected ||
            this.current.workflow.status == CalculationStatus.SharedWaiting;
        case CalculatorActions.SetSalaryDateKind:
          return (this.current.workflow.status == CalculationStatus.Draft ||
            this.current.workflow.status == CalculationStatus.ProDraft ||
            this.current.workflow.status == CalculationStatus.SharedApproved) &&
            (
              InvoicesLogic.isPalkkausManualLike(this.current.info.paymentChannel) ||
              InvoicesLogic.isPalkkausInvoicesLike(this.current.info.paymentChannel) ||
              this.current.info.paymentChannel == PaymentChannel.PalkkausCfaTest ||
              this.current.info.paymentChannel == PaymentChannel.Briox ||
              this.current.info.paymentChannel == PaymentChannel.Askelma ||
              this.current.info.paymentChannel == PaymentChannel.PasseliMerit ||
              this.current.info.paymentChannel == PaymentChannel.Ecom ||
              this.current.info.paymentChannel == PaymentChannel.Hausvise
            );
        default:
          return false;
      }
    }

  }

  /**
   * Returns true, if action (button) is disabled
   * TODO: Add rules based on roles and validation? Pay button logic is in UI.
   */
  public isActionDisabled(action: CalculatorActions): boolean {
    if (!this.current) {
      return true;
    }
    // In read only mode
    if (this.isReadOnlyForced) {
      return true;
    }

    if (this.current.isReadOnly) {
      switch (action) {
        case CalculatorActions.Delete:
          return this.current.workflow.status == CalculationStatus.PaymentStarted;
        case CalculatorActions.Save:
          // add here isValid?
          return this.current.workflow.status == CalculationStatus.PaymentStarted;
        default:
          return true;
      }
    }
  }

  /** Loads the available usecases => The UI shows them as selection. */
  public showUsecasesSelection(): Promise<Calculation> {
    this.usecases = [];
    return this.setStatus(this.usecasesService.getActiveUsecaseListItems(ApiItemType.Calculation).then((list) => {
      if (list.length) {
        this.usecases = list.sort((a, b) => (a.data.order || 100) - (b.data.order || 100));
      } else {
        // No usecases => use default
        this.usecases = null;
      }
      return this.current;
    }));
  }

  /**
   * Sets the given usecase to the current calculation.
   * @param uri Uri for the new usecase.
   */
  public setUsecase(uri: string): void {
    this.setStatus(this.usecasesService.assureUsecase(ApiItemType.Calculation, this.current, uri).then((uc) => {
      this.usecase = uc;
      //this.callEvent("onLoad");
      this.usecases = null;
      return this.current;
    }));
  }


  /**
   * Gets a total of row type(s)
   * @param types A single row type or array of types to filter for calculation.
   */
  public getRowTotal(types: CalculationRowType | CalculationRowType[]) {
    if (!types) {
      return 0;
    }
    if (!Array.isArray(types)) {
      types = [types];
    }
    return Arrays.sum(this.current?.result?.rows || [], (x) => types.indexOf(x.rowType) >= 0 ? x.total : 0);
  }

  /**
   * Shows a report for the current calculation using a modal dialog.
   * @param reportType - Type of report to show
   */
  public showReportDialog(reportType: calcReportType) {
    this.reportsService.showReportModalForCalc(reportType, this.current);
  }

  /**
   * Shows a dialog for modifying an existing Worker:
   * Saves changes and updates the calculation.
   */
  public showWorkerEditDialog() {
    this.uiCrudHelpers.openEditEmploymentDialog(this.current.worker.employmentId, "updateCalc")
      .then((result) => {
        if (result.action === "ok" || result.action === "ok-no-rows") {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.CalcComponent.dialog.showWorkerEditDialogLoading");
          this.updateFromEmployment(result.item.id, result.action === "ok").then(() => {
            loader.dismiss();
          });
        }
      });
  }

  /** Shows infor dialog about the current usecase in the calculation. */
  public showUsecaseInfo(): void {
    this.uiHelpers.openEditDialog("salaxy-components/modals/workflow/UsecaseInfo.html", this.usecase, {
      objUsecase: this.current.usecase,
      canDelete: this.isNew(),
    }, "lg").then((result) => {
      if (result.action == EditDialogKnownActions.Delete && this.isNew()) {
        this.usecase = null;
        this.current.usecase = {};
        return this.showUsecasesSelection();
      }
    });
  }

  /**
   * Shows a dialog during the check of auto taxcard.
   */
  public showRefreshAutoTaxcardDialog() {
    const loader = this.uiHelpers.showLoading();
    this.uiCrudHelpers.refreshAutoTaxcard(this.current.worker.tax?.taxcardId, this.current.workflow?.salaryDate).then(() => {
      loader.dismiss();
      this.recalculate();
    });
  }

  /**
   * Shows a dialog for adding a new Employment relation.
   * If user goes through the wizard the employment relation is saved and set to the calculation.
   */
  public showNewWorkerDialog() {
    this.uiCrudHelpers.createNewEmployment().then((result) => {
      if (result.action === "ok") {
        this.updateFromEmployment(result.item.id, true);
      }
    });
  }

  /**
   * Returns validation for current calculation or empty validation object if the calculation has not been recalculated (validated).
   */
  public get validation() {
    return this.current?.result?.validation || { errors: [], hasAllRequiredFields: true, isValid: true };
  }

  /** Returns true if the payment is possible when the employer pays all.  */
  public get isForcePayAllAllowed(): boolean {
    const obj = this.validation.errors.find((x) =>
      (x.type !== "warning"));
    return !obj;
  }
  /** HACK: This will be removed after moved to useCase based calculators. Placeholder for usecase specific logic in history calculation. */
  public historyUsecase = {
    save: () => {
      this.detailsUrl = "/calc/history/";
      this.current.workflow.status = CalculationStatus.History,
        this.current.workflow.salaryDateKind = SalaryDateKind.InPast;
      this.save();
    },
    recalculate: () => {
      this.current.workflow.status = CalculationStatus.History,
        this.current.workflow.salaryDateKind = SalaryDateKind.InPast;
      this.recalculate();
    }
  };
}